import { Injectable } from '@angular/core';
import { createStore, select, setProp, withProps } from '@ngneat/elf';
import * as localforage from 'localforage';
import {
  IDeleteParams,
  IListParams,
  IStoreProps,
} from 'src/app/talent-litmus-core/common/common.interface';
import { IValuesGame } from './values-game.interface';
import {
  addEntities,
  deleteEntities,
  selectAllEntities,
  selectAllEntitiesApply,
  selectEntity,
  setEntities,
  updateEntities,
  withEntities,
} from '@ngneat/elf-entities';
import { persistState } from '@ngneat/elf-persist-state';
import { BehaviorSubject, Observable } from 'rxjs';
import { debounceTime, map, switchMap, tap } from 'rxjs/operators';
import { resolveMongoObjectId } from 'src/app/shared/utils/resolveMongoObject.utils';
import * as moment from 'moment';
import { REFRESH_TIME_IN_MINUTES } from 'src/app/shared/configs/global-values.constants';
import { ApiBaseService } from 'src/app/shared/services/api/api-base/api-base.service';

// eslint-disable-next-line @typescript-eslint/naming-convention
const STORE_NAME = 'values-games';

const instance = localforage.createInstance({
  driver: localforage.INDEXEDDB,
  name: 'talent-litmus-adminv2',
  version: 1.0,
  storeName: STORE_NAME,
});

const store = createStore(
  { name: STORE_NAME },
  withProps<IStoreProps>({ lastRefreshed: undefined, search: '' }),
  withEntities<IValuesGame, '_id'>({ idKey: '_id' }),
);

export const persist = persistState(store, {
  key: STORE_NAME,
  storage: instance,
});

@Injectable()
export class ValuesGameCoreService {
  private _loading$ = new BehaviorSubject<boolean>(false);
  private _filter$ = store.pipe(select(({ search }) => search));

  /**
   * @name valuesGameList$
   * @description emits stored list of the companies
   */
  valuesGameList$ = store.pipe(selectAllEntities());

  totalCount: number;

  visibleValuesGameList$ = this._filter$.pipe(
    switchMap((filter) => {
      return store.pipe(
        selectAllEntitiesApply({
          filterEntity(valuesGame: IValuesGame) {
            return (
              valuesGame.name
                .toLowerCase()
                .includes(filter.trim().toLowerCase()) ||
              valuesGame._id.toLowerCase().includes(filter.trim().toLowerCase())
            );
          },
        }),
      );
    }),
  );

  public get loading$(): Observable<boolean> {
    return this._loading$.asObservable();
  }

  constructor(private _api: ApiBaseService) {
    this.refresh();
  }

  public updateFilter(search: IStoreProps['search']): void {
    store.update(setProp('search', search));
  }

  public getValuesGames(params?: IListParams): void {
    this._loading$.next(true);
    this._api
      .get(`master-game/list?type=values&limit=1000&skip=0`, params || {})
      .pipe(debounceTime(10000))
      .subscribe((data: IValuesGameListResponse) => {
        this.totalCount = data.totalCount;
        store.update(
          setEntities(data.list),
          setProp('lastRefreshed', new Date()),
        );
        this._loading$.next(false);
      });
  }
  public refresh(forced?: boolean): void {
    if (
      forced ||
      !store.value.lastRefreshed ||
      moment().diff(moment(store.value.lastRefreshed), 'minutes') >
        REFRESH_TIME_IN_MINUTES
    ) {
      this.getValuesGames();
    }
  }

  public getValuesGame(valuesGameId: string): Observable<IValuesGame> {
    return this._api.get(`master-game/${valuesGameId}`);
  }

  public getValuesGameFromCache(valuesGameId: string): Observable<IValuesGame> {
    return store.pipe(selectEntity(valuesGameId));
  }

  public addValuesGame(valuesGame: IValuesGame): Observable<IValuesGame> {
    return this._api
      .post(`master-game/add`, valuesGame)
      .pipe(tap((data: IValuesGame) => store.update(addEntities([data]))));
  }

  public updateValuesGame(valuesGame: IValuesGame): Observable<IValuesGame> {
    return this._api.put(`master-game/edit`, valuesGame).pipe(
      map((data: ArrayBuffer) => data as any),
      tap((data: IValuesGame) =>
        store.update(
          updateEntities(data._id, (entity) => ({
            ...entity,
            name: data.name,
          })),
        ),
      ),
    );
  }

  public deleteValuesGame(data: IDeleteParams): Observable<any> {
    return this._api
      .delete('master-game', { body: data })
      .pipe(tap(() => store.update(deleteEntities(data['_id']))));
  }

  // public getValuesGamePlayers(
  //   valuesGameId: string,
  // ): Observable<IValuesPlayerRank[]> {
  //   return store.pipe(
  //     selectEntity(valuesGameId),
  //     filter((valuesGame: IValuesGame) => !!valuesGame),
  //     switchMap((valuesGame) => {
  //       const divisionId = resolveMongoObjectId(valuesGame.divisionId);
  //       return this._api.get(
  //         `values-players/leaderboard?divisionId=${divisionId}&maxRank=100000`,
  //       );
  //     }),
  //   );
  // }

  public deleteValuesGamePlayers(
    valuesGameId: string,
    _ids: string[],
  ): Observable<any> {
    return store.pipe(
      selectEntity(valuesGameId),
      switchMap((valuesGame) => {
        const divisionId = resolveMongoObjectId(valuesGame);
        const body: IBulkDeletePlayer = {
          divisionId,
          _ids,
        };
        return this._api.delete(`values-players/bulk`, { body });
      }),
    );
  }
}

interface IValuesGameListResponse {
  list: IValuesGame[];
  totalCount: number;
}

export interface IBulkDeletePlayer {
  _ids: string[];
  divisionId: string;
  deleteAll?: boolean;
}
